import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Router, Route, useLocation } from 'react-router-dom';
import { GAP_PACKET_ACCESS_DENIED_ROUTE, INVESTIGATOR_BROCHURE_ACCESS_DENIED_ROUTE, NOT_FOUND_ROUTE } from 'spark-state/common/middleware/navigation/navigationMiddleware';
import { initializeAuth, isReady, isAuthenticating } from 'spark-state/common/app/reducer';
import CtmsRefreshIndicator from 'spark-state/ctmsRefreshIndicator';
import ApplicationBody from 'spark-ui/ui-kitchen/ingredients/base/ApplicationBody';
import Canvas from 'spark-ui/ui-kitchen/ingredients/base/Canvas';
import SparkFooter from 'spark-views/common/footer/SparkFooter';
import MessageListener from 'spark-views/common/messages/MessageListener';
import SparkHeader from 'spark-views/common/header/SparkHeader';
import SafetyLetterAdminRoute from 'spark-views/safetyLetter/admin/SafetyLetterAdminRoute';
import SafetyLetterRecipientsRoute from 'spark-views/safetyLetter/notifications/SafetyLetterRecipientsRoute';
import EnterpriseRoute from 'spark-views/enterprise/EnterpriseRoute';
import ReportTypeRoute from 'spark-views/configuration/reportType/ReportTypeRoute';
import NotFound from './NotFound';
import BulkDownloadRoute from 'spark-views/bulkDownload/BulkDownloadRoute';
import AuditReportsRoute from 'spark-views/safetyLetter/admin/report/AuditReportsRoute';
import OverdueAckRoute from 'spark-views/safetyLetter/admin/overdueAck/OverdueAckRoute';
import SnDraftRoute from 'spark-views/safetyLetter/admin/details/SnDraft/SnDraftRoute';
import ManageRolesRoute from 'spark-views/safetyLetter/admin/manageRoles/ManageRolesRoute';
import GapPacketRoute from 'spark-views/gapPacketManagement/GapPacketRoute';
import IBAdminRoute from 'spark-views/InvestigatorBrochure/ibAdmin/IBAdminRoute';
import IBRecipientsRoute from 'spark-views/InvestigatorBrochure/ibNotifications/IBRecipientsRoute';
import UserGuidesRoute from 'spark-views/userGuides/UserGuidesRoute';
import GapPacketAccessDenied from './GapPacketAccessDenied';
import InvestigatorBrochureAccessDenied from './InvestigatorBrochureAccessDenied';

/**
 * React container for the application.
 * Waits to render anything until the app is finished with both
 * authenticating the user, and loading their settings.
 *
 * Rendering the application before the user settings are loaded would
 * result in seeing a lot of the UI rerendering based on user permissions.
 */
function AppContent() {
	const location = useLocation();
	//SAF-18432- To hide CTMS refresh indicator when user is on User Guide Page.
	const isUserGuidePage = location.pathname.includes('/userGuides');

	return (
		<React.Fragment>
			<SparkHeader />
			<ApplicationBody>
				<Canvas>
					{/** 
                        Aside from the NotFound route, all of these Route components
                        should be dynamically loaded (i.e., take advantage of code-splitting)
                        to improve performance and not load unnecessary code.
					*/}
					
					<EnterpriseRoute />
					<SafetyLetterAdminRoute />
					<SafetyLetterRecipientsRoute />
					<ReportTypeRoute />
					<BulkDownloadRoute /> 
					<AuditReportsRoute />
					<OverdueAckRoute />
					<SnDraftRoute/>
					<ManageRolesRoute/>
				<GapPacketRoute />
				<IBAdminRoute /> {/*SAf-17154added IB Route */}
				<IBRecipientsRoute />
				<UserGuidesRoute />
				<Route exact path={NOT_FOUND_ROUTE} component={NotFound} />
				<Route exact path={GAP_PACKET_ACCESS_DENIED_ROUTE} component={GapPacketAccessDenied} />
				<Route exact path={INVESTIGATOR_BROCHURE_ACCESS_DENIED_ROUTE} component={InvestigatorBrochureAccessDenied} />
				</Canvas>
				{!isUserGuidePage && <CtmsRefreshIndicator/>}
				<SparkFooter />
			</ApplicationBody>
			<MessageListener />
		</React.Fragment>
	);
}

function App({ history }) {
	const dispatch = useDispatch();
	const isAuthenticatingState = useSelector(state => isAuthenticating(state));
	const isAppReady = useSelector(state => isReady(state));

	useEffect(() => {
		dispatch(initializeAuth());
	}, [dispatch]);

	if (isAuthenticatingState || !isAppReady) {
		return null;
	}

	return (
		<Router history={history}>
			<AppContent />
		</Router>
	);
}

export default React.memo(App);
